import React from 'react';

import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import { LazyImage } from '@waglabs/lazyload';
import { Typography } from '@waglabs/typography';
import { Container } from '@waglabs/container';

import appImage from '../../../../assets/how-wag-works/how-wag-works-app.jpg';
import pawsIcon from '../../../../assets/how-wag-works/paws.svg';
import userCheckIcon from '../../../../assets/how-wag-works/user_check.svg';
import mapPinIcon from '../../../../assets/how-wag-works/map_pin.svg';
import chatHeartIcon from '../../../../assets/how-wag-works/chat_hearth.svg';

const Step = ({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: React.ReactNode;
}) => (
  <Flex
    mb={[3, null, 4]}
    alignItems="flex-start"
  >
    <Box
      width={['34px', null, '34px']}
      height={['34px', null, '34px']}
      flex={['0 0 34px', null, '0 0 34px']}
      mr={2}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <LazyImage
        src={icon}
        alt="step icon"
        width={['100%', null, '100%']}
        height={['100%', null, '100%']}
        imageProps={{
          style: { objectFit: 'contain', display: 'block' },
        }}
      />
    </Box>
    <Box>
      <Typography
        variant="h4"
        m={0}
        fontSize={['16px', null, '18px']}
        color="#474747"
        fontWeight={700}
      >
        {title}
      </Typography>
      <Typography
        m={0}
        mt={1}
        color="#474747"
        fontSize={['14px', null, '16px']}
      >
        {description}
      </Typography>
    </Box>
  </Flex>
);

export const HowWagWorksSection = () => (
  <Container
    pt={['42px', null, '64px']}
    pb={['200px', null, '10px']}
  >
    <Flex
      flexDirection={['column', null, 'row']}
      alignItems={['center', null, 'flex-start']}
      justifyContent={['center', null, 'center']}
      flexWrap={['wrap', null, 'nowrap']}
    >
      {/* Left: App image */}
      <Box
        order={[2, null, 1]}
        flex={['0 0 auto', null, '0 0 520px']}
        flexBasis={['auto', null, '520px']}
        flexShrink={[0, null, 0]}
        width={['100%', null, '520px']}
        maxWidth={['100%', null, '520px']}
        display="flex"
        justifyContent={['center', null, 'flex-end']}
        mt={[4, null, 0]}
        mb={['80px', null, 0]}
        zIndex={0}
      >
        <LazyImage
          src={appImage}
          alt="How Wag works app"
          width={['100%', null, '100%']}
          height={['auto', null, 'auto']}
          imageProps={{
            style: {
              maxWidth: '520px',
              width: '100%',
              height: 'auto',
              display: 'block',
            },
          }}
        />
      </Box>

      {/* Right: Copy and steps */}
      <Box
        order={[1, null, 2]}
        flex={['0 0 auto', null, '1 1 0']}
        width={['100%', null, 'auto']}
        maxWidth={['100%', null, '650px']}
        minWidth={['auto', null, 0]}
        mb={[0, 0, 0]}
        ml={[0, null, '26px']}
        px={[2, 2, 0]}
        zIndex={1}
      >
        <Typography
          variant="h2"
          color="#222222"
          m={0}
          mb={[2, null, 3]}
        >
          How Wag! works
        </Typography>
        <Typography
          color="#474747"
          m={0}
          mb={[3, null, 4]}
          fontSize={['16px', null, '18px']}
        >
          Easily find and book trusted Pet Caregivers near you
        </Typography>

        <Box>
          <Step
            icon={pawsIcon}
            title="Pick a service"
            description={(
              <>
                Whether it’s Walks, Drop-Ins, Sittings, or Boardings,
                choose the service that best fits your pet’s needs.
              </>
            )}
          />
          <Step
            icon={userCheckIcon}
            title="Choose your Caregiver"
            description={(
              <>
                Select from committed Caregivers who understand your
                requirements. Chat directly to ensure the perfect match!
              </>
            )}
          />
          <Step
            icon={mapPinIcon}
            title="Follow along in the app"
            description={(
              <>
                Download the Wag! App to track walks via GPS, communicate with your
                Caregiver, and enjoy real-time photo and video updates.
              </>
            )}
          />
          <Step
            icon={chatHeartIcon}
            title="Rate and review"
            description={(
              <>Share feedback to help other Pet Parents find their perfect Caregiver.</>
            )}
          />
        </Box>
      </Box>
    </Flex>
  </Container>
);
